import { Platform } from 'react-native';
import type { QueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from './query-keys';

export function syncNativeNotificationBadge(count: number) {
  if (Platform.OS === 'web') return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const Notifications = require('expo-notifications');
    if (typeof Notifications.setBadgeCountAsync === 'function') {
      void Notifications.setBadgeCountAsync(Math.max(0, count)).catch(() => {});
    }
  } catch {
    // Ignore badge sync failures; push payloads still carry a badge value.
  }
}

/**
 * Clears the app-icon badge and dismisses any delivered notifications.
 *
 * Called on sign-out and account deletion so stale push notifications no
 * longer linger on the app icon for the next (anonymous) session. Optionally
 * resets the cached unread count when a QueryClient is provided. No-op on web,
 * mirroring `syncNativeNotificationBadge`.
 */
export function clearNotificationBadge(queryClient?: QueryClient) {
  if (queryClient) {
    queryClient.setQueryData(QUERY_KEYS.NOTIFICATION_UNREAD_COUNT, 0);
  }

  if (Platform.OS === 'web') return;

  // Reset the app-icon badge to zero.
  syncNativeNotificationBadge(0);

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const Notifications = require('expo-notifications');
    if (typeof Notifications.dismissAllNotificationsAsync === 'function') {
      void Notifications.dismissAllNotificationsAsync().catch(() => {});
    }
  } catch {
    // Ignore dismissal failures; the badge has already been reset above.
  }
}

export function setNotificationUnreadCount(
  queryClient: QueryClient,
  count: number,
) {
  const nextCount = Math.max(0, count);
  queryClient.setQueryData(QUERY_KEYS.NOTIFICATION_UNREAD_COUNT, nextCount);
  syncNativeNotificationBadge(nextCount);
  return nextCount;
}

export function updateNotificationUnreadCount(
  queryClient: QueryClient,
  updater: (current: number) => number,
) {
  const current = queryClient.getQueryData<number>(
    QUERY_KEYS.NOTIFICATION_UNREAD_COUNT,
  );
  if (typeof current !== 'number') {
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT,
    });
    return null;
  }

  const nextCount = Math.max(0, updater(current));
  queryClient.setQueryData(QUERY_KEYS.NOTIFICATION_UNREAD_COUNT, nextCount);
  syncNativeNotificationBadge(nextCount);
  return nextCount;
}
